import React, { createContext, useContext, useEffect, useState } from "react";
import { trackEvent } from "../lib/analytics";
import { api } from "../api";
import { toast } from "sonner";

const CartContext = createContext(null);

const round2 = (n) => Math.round(n * 100) / 100;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dl_cart") || "[]"); } catch { return []; }
  });
  const [discount, setDiscount] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dl_discount") || "null"); } catch { return null; }
  });
  const [open, setOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    localStorage.setItem("dl_cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    try {
      if (discount) localStorage.setItem("dl_discount", JSON.stringify(discount));
      else localStorage.removeItem("dl_discount");
    } catch { /* localStorage niedostępny */ }
  }, [discount]);

  const add = (product) => {
    setItems((prev) => {
      if (prev.find((i) => i.id === product.id)) return prev;
      return [...prev, { id: product.id, title: product.title, author: product.author, price: product.price, format: product.format, cover_image: product.cover_image || null }];
    });
    trackEvent("add_to_cart", { product_id: product.id });
    toast.success(`Dodano „${product.title}" do koszyka`);
  };

  const remove = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const clear = () => { setItems([]); setDiscount(null); };
  const count = items.length;

  // rabat liczony tak samo jak na serwerze: cena każdej pozycji zaokrąglona po obniżce
  const subtotal = round2(items.reduce((s, i) => s + i.price, 0));
  const totalAfter = discount
    ? round2(items.reduce((s, i) => s + round2(i.price * (1 - discount.percent / 100)), 0))
    : subtotal;
  const discountAmount = round2(subtotal - totalAfter);
  const total = totalAfter;

  const applyCode = async (code) => {
    try {
      const { data } = await api.post("/discount/validate", { code });
      setDiscount({ code: data.code, percent: data.percent });
      toast.success(`Kod ${data.code} zastosowany — rabat ${data.percent}%`);
      return true;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nie udało się sprawdzić kodu. Spróbuj ponownie.");
      return false;
    }
  };
  const removeCode = () => setDiscount(null);

  const checkout = async (form) => {
    if (!items.length) return null;
    setCheckingOut(true);
    try {
      const token = (() => { try { return localStorage.getItem("dl_customer_token") || ""; } catch { return ""; } })();
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.id, quantity: 1 })),
        email: form.email,
        name: form.name,
        phone: form.phone,
        street: form.street,
        postal_code: form.postalCode,
        city: form.city,
        company_name: form.companyName || "",
        nip: form.nip || "",
        origin_url: window.location.origin,
        payment_method: form.method,
        discount_code: discount?.code || "",
        delivery_method: "electronic",
        accept_terms: !!form.acceptTerms,
        accept_digital_waiver: !!form.acceptWaiver,
        use_balance: !!form.useBalance,
      }, form.useBalance && token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
      if (data.paid_with_balance) {          // całość opłacona saldem — bez bramki płatniczej
        clear();
        window.location.href = `/platnosc/sukces?order=${data.order_id}&saldo=1`;
        return data;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return data;
      }
      clear();
      return data;
    } catch (e) {
      const d = e.response?.data?.detail;
      if (typeof d === "string" && d.toLowerCase().includes("kod")) setDiscount(null);
      toast.error(typeof d === "string" ? d : "Nie udało się złożyć zamówienia. Spróbuj ponownie.");
      return null;
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <CartContext.Provider value={{ items, add, remove, clear, count, subtotal, discount, discountAmount, total, applyCode, removeCode, open, setOpen, checkout, checkingOut }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
